import styled from '@emotion/styled';
import { gettext } from '@eventbrite/i18n';
import {
    HouseIcon,
    SelectField,
    TextField,
    Typography,
} from '@eventbrite/marmalade';
import React, { useCallback, useMemo } from 'react';
import {
    Controller,
    FieldValues,
    Path,
    PathValue,
    useFormContext,
} from 'react-hook-form';
import { BREAKPOINTS, colors } from '../../config';
import { useFormValues } from '../../hooks';
import { RegionFormField } from '../RegionFormField';
import { useCountries } from '../useCountries';
import { useRegions } from '../useRegions';
import {
    getPostalError,
    getPostalLabel,
    getRegionError,
    getRegionLabel,
    isPostalRequired,
    isRegionRequired,
} from '../utils';

export enum MoreOptionsLocationFieldsTestId {
    Region = 'MoreOptionsLocationRegion',
    City = 'MoreOptionsLocationCity',
    ZipCode = 'MoreOptionsLocationZipCode',
    Country = 'MoreOptionsLocationCountry',
    VenueName = 'MoreOptionsLocationVenueName',
    Address1 = 'MoreOptionsLocationAddress1',
    Address2 = 'MoreOptionsLocationAddress2',
    Button = 'MoreOptionsLocationButton',
}

export interface MoreOptionsLocationFieldsProps {
    isValidRegion: boolean;
    onGeocoding: () => Promise<void> | void;
    /**
     * ISO 3166-1 Alpha-2 country codes for countries that should be filtered
     * from country dropdown. This prop shadows a feature flag in the consumer app.
     * Example: new Set(['BR']) to filter Brazil.
     */
    blacklistedCountries?: Set<string>;
}

export const MoreOptionsLocationFields = <T extends FieldValues>({
    isValidRegion,
    onGeocoding,
    blacklistedCountries,
}: MoreOptionsLocationFieldsProps) => {
    const { control, setValue } = useFormContext<T>();
    const { showVenueOptions, venue } = useFormValues<T>();
    const { data: regions = [] } = useRegions();
    const { data: countries = [] } = useCountries(
        5000,
        blacklistedCountries ?? new Set(),
        venue.address.country,
    );
    const openVenueOptions = useCallback(() => {
        setValue('showVenueOptions' as Path<T>, true as PathValue<T, Path<T>>);
    }, [setValue]);

    const countryRegionList = useMemo(
        () =>
            regions
                .filter((r) => r.country_code === venue.address.country)
                .map((region) => ({
                    value: region.code,
                    label: region.label,
                })),
        [regions, venue.address.country],
    );

    const countryOptions = useMemo(
        () =>
            countries.map((country) => ({
                value: country.code,
                label: country.label,
            })),
        [countries],
    );

    const postalRequired = isPostalRequired(countries, venue.address.country);
    const postalLabel = getPostalLabel(venue.address.country);
    const postalError = getPostalError(venue.address.country);

    const regionRequired =
        isRegionRequired(venue.address.country) || !isValidRegion;
    const regionLabel = getRegionLabel(venue.address.country);
    const regionError = getRegionError(venue.address.country);

    return showVenueOptions ? (
        <FieldsContainer>
            <FullWidthFieldItem>
                <Controller
                    /* react-hook-form does not manage well generic components, see the comment above */
                    name={`venue.name` as Path<T>}
                    control={control}
                    rules={{
                        required: {
                            value: true,
                            message: gettext(
                                'Venue name is required',
                            ).toString(),
                        },
                    }}
                    render={({ field, fieldState: { error } }) => (
                        <TextField
                            autoComplete="off"
                            data-testid={
                                MoreOptionsLocationFieldsTestId.VenueName
                            }
                            required
                            hasError={!!error}
                            helperText={error && error.message}
                            id="event-simplification-form-venue-name"
                            label={gettext('Venue Name').toString()}
                            maxLength={500}
                            {...field}
                            onBlur={onGeocoding}
                        />
                    )}
                />
            </FullWidthFieldItem>

            <ThirdToFullWidthFieldItem>
                <Controller
                    name={`venue.address.address1` as Path<T>}
                    control={control}
                    rules={{
                        required: {
                            value: true,
                            message: gettext('Address is required').toString(),
                        },
                    }}
                    render={({ field, fieldState: { error } }) => (
                        <TextField
                            autoComplete="off"
                            data-testid={
                                MoreOptionsLocationFieldsTestId.Address1
                            }
                            required
                            hasError={!!error}
                            helperText={error && error.message}
                            id="event-simplification-form-venue-address1"
                            label={gettext('Address 1').toString()}
                            {...field}
                            onBlur={onGeocoding}
                        />
                    )}
                />
            </ThirdToFullWidthFieldItem>

            <ThirdToFullWidthFieldItem>
                <Controller
                    name={`venue.address.address2` as Path<T>}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <TextField
                            autoComplete="off"
                            data-testid={
                                MoreOptionsLocationFieldsTestId.Address2
                            }
                            hasError={!!error}
                            helperText={error && error.message}
                            id="event-simplification-form-venue-address2"
                            label={gettext('Address 2').toString()}
                            maxLength={100}
                            {...field}
                            onBlur={onGeocoding}
                        />
                    )}
                />
            </ThirdToFullWidthFieldItem>

            <ThirdToHalfWidthFieldItem>
                <Controller
                    name={`venue.address.city` as Path<T>}
                    control={control}
                    rules={{
                        required: {
                            value: true,
                            message: gettext('City is required').toString(),
                        },
                    }}
                    render={({ field, fieldState: { error } }) => (
                        <TextField
                            autoComplete="off"
                            data-testid={MoreOptionsLocationFieldsTestId.City}
                            required
                            hasError={!!error}
                            helperText={error && error.message}
                            id="event-simplification-form-venue-city"
                            label={gettext('City').toString()}
                            {...field}
                            onBlur={onGeocoding}
                        />
                    )}
                />
            </ThirdToHalfWidthFieldItem>

            <ThirdToHalfWidthFieldItem>
                <Controller
                    name={`venue.address.region` as Path<T>}
                    control={control}
                    rules={{
                        validate: (value: string) =>
                            !value && regionRequired ? regionError : undefined,
                    }}
                    render={({ field, fieldState: { error } }) => (
                        <RegionFormField
                            required={regionRequired}
                            hasError={!!error}
                            helperText={error && error.message}
                            aria-label={regionLabel}
                            id={MoreOptionsLocationFieldsTestId.Region}
                            label={regionLabel}
                            options={countryRegionList}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={onGeocoding}
                        />
                    )}
                />
            </ThirdToHalfWidthFieldItem>

            {postalRequired && (
                <ThirdToHalfWidthFieldItem>
                    <Controller
                        name={`venue.address.postalCode` as Path<T>}
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: postalError,
                            },
                        }}
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                autoComplete="off"
                                data-testid={
                                    MoreOptionsLocationFieldsTestId.ZipCode
                                }
                                required
                                hasError={!!error}
                                helperText={error && error.message}
                                id="event-simplification-form-venue-zipcode"
                                label={postalLabel}
                                {...field}
                                onBlur={onGeocoding}
                            />
                        )}
                    />
                </ThirdToHalfWidthFieldItem>
            )}

            <ThirdToHalfWidthFieldItem>
                <Controller
                    name={`venue.address.country` as Path<T>}
                    control={control}
                    rules={{
                        required: {
                            value: true,
                            message: gettext('Country is required').toString(),
                        },
                    }}
                    render={({ field, fieldState: { error } }) => (
                        <SelectField
                            data-testid={
                                MoreOptionsLocationFieldsTestId.Country
                            }
                            required
                            hasError={!!error}
                            helperText={error && error.message}
                            id="event-simplification-form-venue-country"
                            label={gettext('Country').toString()}
                            options={countryOptions}
                            {...field}
                            onBlur={onGeocoding}
                        />
                    )}
                />
            </ThirdToHalfWidthFieldItem>
        </FieldsContainer>
    ) : (
        <ButtonContainer>
            <Button
                onClick={openVenueOptions}
                data-testid={MoreOptionsLocationFieldsTestId.Button}
            >
                <HouseIcon color={colors['ui-700']} size="xsmall" />

                <Typography
                    as="span"
                    variant="body-md-bold"
                    style={{ color: colors['ui-700'] }}
                >
                    {gettext('Add location details').toString()}
                </Typography>
            </Button>
        </ButtonContainer>
    );
};

const FieldsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-items: start;
    gap: 1rem;
    margin-bottom: 1rem;
    margin-top: 0.75rem;
`;

const FullWidthFieldItem = styled.div`
    width: 100%;
`;

const ThirdToFullWidthFieldItem = styled.div`
    width: 100%;

    @media (min-width: ${BREAKPOINTS.MEDIUM_NARROW}px) {
        width: calc(33.33% - 0.75rem);
    }
`;

const ThirdToHalfWidthFieldItem = styled.div`
    width: calc(50% - 0.5rem);

    @media (min-width: ${BREAKPOINTS.MEDIUM_NARROW}px) {
        width: calc(33.33% - 0.75rem);
    }
`;

const Button = styled.button`
    display: flex;
    gap: 0.5rem;
    align-items: center;
    padding: 0.25rem;
    border: none;
    line-height: normal;
    border-radius: 0.25rem;
    background-color: ${colors['ui-100']};
    margin-top: 0.5rem;

    &:hover {
        background-color: ${colors['ui-100']};
    }

    @media (min-width: ${BREAKPOINTS.MEDIUM_NARROW}px) {
        background-color: transparent;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    margin-bottom: 1.5rem;
`;
